import { PushPin, PushPinOutlined } from '@mui/icons-material'
import {
  alpha,
  Box,
  Divider,
  Drawer,
  IconButton,
  List,
  Stack,
  useTheme,
} from '@mui/material'
import { nanoid } from 'nanoid'
import { useMemo } from 'react'
import { useLocation } from 'react-router-dom'

import GuidanceDrawer from '~/features/onboarding/components/guidanceDrawer'
import EElement from '~/features/ui/models'
import {
  SIDE_NAV_GUIDANCE_ITEM_CLASS_NAME,
  SIDE_NAV_HEADER_CLASS_NAME,
  SIDE_NAV_PIN_BUTTON_CLASS_NAME,
  SIDE_NAV_WIDTH,
  SIDE_NAV_WIDTH_COLLAPSED,
} from '~/layouts/account/header/nav/side/constants'
import GuidanceItem from '~/layouts/account/header/nav/side/guidanceItem'
import NavItem from '~/layouts/account/header/nav/side/navItem'
import useNavItems from '~/layouts/account/hooks/useNavItems'
import EAccountGridAreas from '~/layouts/account/models'
import { EAdminPortalClientPath } from '~/models/api'
import { store } from '~/store'
import rem from '~/utils/ui'

type TProps = {
  isOpen: boolean
  close: () => void
}

function SideNav({ isOpen, close }: TProps) {
  const theme = useTheme()
  const navItems = useNavItems()

  const location = useLocation()
  const isNotAllowedToSeeGuidance = useMemo(
    () => location.pathname.includes(EAdminPortalClientPath.admin),
    [location],
  )
  const [isGuidanceOpen, isSideNavPinned, setUIElements] = store((state) => [
    state.UIElements.isGuidanceOpen,
    state.UIElements.isSideNavPinned,
    state.setUIElements,
  ])

  const openGuidance = () => {
    setUIElements({ isGuidanceOpen: true })
  }

  const closeGuidance = () => {
    setUIElements({ isGuidanceOpen: false })
  }

  return (
    <Drawer
      variant="permanent"
      id={EElement.accountSideNav}
      sx={{
        gridArea: EAccountGridAreas.NAV,
        transition: 'width 0.3s ease',
        minHeight: rem(1),
        zIndex: theme.zIndex.modal - 1,
        width: {
          xxxs: '100%',
          md: rem(isSideNavPinned ? SIDE_NAV_WIDTH : SIDE_NAV_WIDTH_COLLAPSED),
        },
        '&:hover': {
          width: { md: rem(SIDE_NAV_WIDTH) },
        },
        position: 'relative',
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          position: { xxxs: 'absolute', md: 'static' },
          boxSizing: 'border-box',
          border: 'none',
          backgroundColor: theme.palette.menuBackground,
          zIndex: theme.zIndex.drawer,
          width: { xxxs: isOpen ? '100vw' : 0, md: rem(SIDE_NAV_WIDTH) },
          overflowX: 'hidden',
          transition: 'width 0.3s ease-out',
          '& .MuiListItemButton-root': {
            minWidth: { md: 'max-content' },
            '& *': {
              transition: 'opacity 0.3s ease-out',
            },
          },
          '& .MuiCollapse-root': {
            overflow: 'hidden',
            transition: 'max-height 0.3s ease-out',
            maxHeight: { md: rem(1200) },
          },
          [`& .${SIDE_NAV_HEADER_CLASS_NAME}`]: {
            transition: 'all 0.3s ease-out',
          },
          '&:not(:hover)': isSideNavPinned
            ? {}
            : {
                width: { md: rem(SIDE_NAV_WIDTH_COLLAPSED) },
                '& .MuiListItemButton-root *:not(:first-of-type)': {
                  opacity: { md: 0 },
                },
                '& .MuiCollapse-root': {
                  maxHeight: { md: 0 },
                },
                [`& .${SIDE_NAV_HEADER_CLASS_NAME}`]: {
                  py: 0,
                  opacity: 0,
                  overflow: 'hidden',
                },
                [`& .${SIDE_NAV_PIN_BUTTON_CLASS_NAME}`]: {
                  display: 'none',
                },
                [`& .${SIDE_NAV_GUIDANCE_ITEM_CLASS_NAME}`]: {
                  border: { md: 'none' },
                },
              },
        },
      }}
    >
      <IconButton
        className={SIDE_NAV_PIN_BUTTON_CLASS_NAME}
        onClick={() => setUIElements({ isSideNavPinned: !isSideNavPinned })}
        sx={{
          display: { xxxs: 'none', md: 'flex' },
          color: theme.palette.marketing.border2,
          position: 'absolute',
          top: 0,
          right: rem(10),
          zIndex: theme.zIndex.tooltip,
          '.MuiSvgIcon-root': {
            fontSize: rem(20),
          },
        }}
      >
        {isSideNavPinned ? <PushPin /> : <PushPinOutlined />}
      </IconButton>
      <Stack
        spacing={2}
        sx={{
          pt: rem(13),
          pb: 0,
          alignItems: 'center',
          height: '100%',
          pl: isSideNavPinned ? rem(23) : { xxxs: rem(23), md: rem(16) },
          transition: 'padding 0.3s ease-out',
          '&:hover': {
            pl: rem(23),
          },
        }}
      >
        <List
          component="nav"
          sx={{
            width: '100%',
            height: '100%',
            py: 0,
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <Box
            sx={{
              flex: '1 1 auto',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: { xxxs: 'flex-start', md: 'space-between' },
            }}
          >
            <Box
              sx={{
                flex: '1 1 auto',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: { xxxs: 'flex-start', md: 'space-between' },
              }}
            >
              <Box>
                {navItems
                  .filter((navItem) => !navItem.isOnBottom)
                  .map((navItem) => (
                    <NavItem key={nanoid()} navItem={navItem} close={close} />
                  ))}
              </Box>
              <Box>
                <Divider
                  sx={{
                    backgroundColor: alpha(theme.palette.menuFont, 0.2),
                    marginLeft: rem(-23),
                    marginY: rem(12),
                  }}
                />
                {isNotAllowedToSeeGuidance ? null : (
                  <GuidanceItem onOpen={openGuidance} />
                )}
                {navItems
                  .filter((navItem) => navItem.isOnBottom)
                  .map((navItem) => (
                    <NavItem key={nanoid()} navItem={navItem} close={close} />
                  ))}
              </Box>
            </Box>
          </Box>
        </List>
      </Stack>
      {isNotAllowedToSeeGuidance ? null : (
        <GuidanceDrawer
          open={isGuidanceOpen}
          onClose={closeGuidance}
          onMenuClose={close}
        />
      )}
    </Drawer>
  )
}

export default SideNav
